import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const token = api.getToken();
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await api.getCurrentUser();
        if (response.success && response.data) {
          setUser(response.data);
          setPermissions(response.data.permissions || []);
          setIsAuthenticated(true);
          
          // Store in localStorage for backward compatibility
          localStorage.setItem('user_role', response.data.role);
          localStorage.setItem('user_permissions', JSON.stringify(response.data.permissions || []));
          localStorage.setItem('user_id', response.data.id);
        }
      } catch (error) {
        console.error('Failed to load user:', error);
        api.logout();
        setUser(null);
        setPermissions([]);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.login(email, password);
      if (response.success && response.data) {
        setUser(response.data.user);
        setPermissions(response.data.user.permissions || []);
        setIsAuthenticated(true);
        
        localStorage.setItem('user_role', response.data.user.role);
        localStorage.setItem('user_permissions', JSON.stringify(response.data.user.permissions || []));
        localStorage.setItem('user_id', response.data.user.id);
        
        return { success: true };
      }
      return { success: false, error: response.error || 'Login failed' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const register = async (email, password) => {
    try {
      const response = await api.register(email, password);
      return response;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    api.logout();
    setUser(null);
    setPermissions([]);
    setIsAuthenticated(false);
  };

  const hasPermission = (permission) => {
    if (user?.role === 'admin') return true;
    return permissions.includes(permission);
  };

  const canRead = (resource) => {
    return hasPermission(`${resource}.read`);
  };

  const canCreate = (resource) => {
    return hasPermission(`${resource}.create`);
  };

  const canUpdate = (resource) => {
    return hasPermission(`${resource}.update`);
  };

  const canDelete = (resource) => {
    return hasPermission(`${resource}.delete`);
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const refreshUser = async () => {
    try {
      const response = await api.getCurrentUser();
      if (response.success && response.data) {
        setUser(response.data);
        setPermissions(response.data.permissions || []);
        setIsAuthenticated(true);
        
        localStorage.setItem('user_role', response.data.role);
        localStorage.setItem('user_permissions', JSON.stringify(response.data.permissions || []));
        localStorage.setItem('user_id', response.data.id);
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const value = {
    user,
    permissions,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    hasPermission,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    isAdmin,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

